import axios from "axios";
import config from "../config";

const serverurl = config.apiUrl;
const serverConfig = { withCredentials: true };

/**
 * Fetch AI-generated action item suggestions for a node.
 * @param {string} nodeId - The post/node ID
 * @returns {Promise<{ reasoning: string, actions: string[] }>}
 */
export const getAiSuggestions = async (nodeId, profile = "strategist", force = false) => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/node/${nodeId}/suggest`,
    { profile, force },
    serverConfig
  );
  return data;
};

/**
 * Mark the strategist cache entry for a node as committed.
 * @param {string} nodeId
 */
export const markStrategyCommitted = async (nodeId) => {
  await axios.patch(
    `${serverurl}/api/v1/ai/node/${nodeId}/suggest/committed`,
    {},
    serverConfig
  );
};

/**
 * Fetch all past analyses for a node, newest first.
 * @param {string} nodeId
 * @returns {Promise<object[]>}
 */
export const getNodeAnalyses = async (nodeId) => {
  const { data } = await axios.get(
    `${serverurl}/api/v1/ai/node/${nodeId}/analyses`,
    serverConfig
  );
  return data.analyses;
};

/**
 * Run a full journey analysis on a node.
 * 7-day cooldown enforced server-side.
 * @param {string} nodeId
 * @returns {Promise<{ analysis: object, deltas: object|null, previousAnalyzedAt: string|null }>}
 */
/**
 * Phase 1: get opening + questions from Strategist.
 */
/**
 * Send a chat message and get a reply.
 */
export const sendChatMessage = async (nodeId, messages, profile = null) => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/node/${nodeId}/chat`,
    { messages, profile },
    serverConfig
  );
  return data;
};

export const getStrategistQuestions = async (nodeId, conversationHistory = [], currentAnswers = null, chatContext = []) => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/node/${nodeId}/strategist/questions`,
    { conversationHistory, currentAnswers, chatContext },
    serverConfig
  );
  return data;
};

/**
 * Phase 2: submit answers + history, get reasoning + actions.
 */
export const getStrategistStrategy = async (nodeId, questionsAndAnswers, conversationHistory = [], chatContext = []) => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/node/${nodeId}/strategist/strategy`,
    { questionsAndAnswers, conversationHistory, chatContext },
    serverConfig
  );
  return data;
};

export const getVisionAssistQuestions = async (planId, conversationHistory = [], currentAnswers = null, direction = "") => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/plan/${planId}/assist/questions`,
    { conversationHistory, currentAnswers, direction },
    serverConfig
  );
  return data;
};

export const getVisionAssistRecords = async (planId, questionsAndAnswers) => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/plan/${planId}/assist/records`,
    { questionsAndAnswers },
    serverConfig
  );
  return data;
};

export const getVisionTodoSuggestions = async (planId, activeTodos = []) => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/plan/${planId}/todos/suggest`,
    { activeTodos },
    serverConfig
  );
  return data;
};

export const runNodeAnalysis = async (nodeId) => {
  const { data } = await axios.post(
    `${serverurl}/api/v1/ai/node/${nodeId}/analyze`,
    {},
    serverConfig
  );
  return {
    analysis: data.analysis,
    deltas: data.deltas,
    previousAnalyzedAt: data.previousAnalyzedAt,
  };
};
